import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [isCaseStudiesOpen, setIsCaseStudiesOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsToolsOpen(false);
    setIsCaseStudiesOpen(false);
  }, [location.pathname]);
  const isActive = (path: string) => location.pathname === path;
  const serviceLinks = [{
    name: 'AI Lead Conversion',
    path: '/ai-lead-conversion'
  }, {
    name: 'Custom Automations',
    path: '/custom-automations'
  }, {
    name: 'CRM & Business Tools',
    path: '/crm-business-tools'
  }, {
    name: 'Reputation Management',
    path: '/reputation-management'
  }, {
    name: 'Ad Campaigns',
    path: '/ad-campaigns'
  }, {
    name: 'Web Design & SEO',
    path: '/web-design-seo'
  }, {
    name: 'Google Business Profile Setup',
    path: '/google-business-profile-setup'
  }];
  const toolLinks = [{
    name: 'Gmail Signature Generator',
    path: '/tools/gmail-signature-generator'
  }];
  const caseStudyLinks = [{
    name: 'All Case Studies',
    path: '/case-studies'
  }, {
    name: 'TFP Landscaping',
    path: '/case-studies/tfp-landscaping'
  }, {
    name: 'Finish Coat Painting',
    path: '/case-studies/finish-coat-painting'
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-viridia-black/90 backdrop-blur-md py-3 shadow-lg' : 'bg-transparent py-5'}`} data-id="element-254">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-255">
        <div className="flex justify-between items-center" data-id="element-256">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-id="element-257">
            <img src="https://cdn.magicpatterns.com/uploads/dBgonnHgNocdZqWCx2rT29/VA-Logo_(1).png" alt="Viridia Analytics" className="h-14 w-auto object-contain transition-transform group-hover:scale-105" data-id="element-258" />
            <span aria-hidden="true" className="text-white font-bold text-xl tracking-wide hidden sm:block" data-id="element-259">
              VIRIDIA ANALYTICS
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav aria-label="Main navigation" className="hidden md:flex items-center gap-8" data-id="element-260">
            <Link to="/" className={`text-sm font-medium transition-colors hover:text-viridia ${isActive('/') ? 'text-viridia' : 'text-gray-300'}`} data-id="element-261">
              Home
            </Link>

            <Link to="/about" className={`text-sm font-medium transition-colors hover:text-viridia ${isActive('/about') ? 'text-viridia' : 'text-gray-300'}`} data-id="element-262">
              About
            </Link>

            {/* Services Dropdown */}
            <div className="relative" onMouseEnter={() => setIsServicesOpen(true)} onMouseLeave={() => setIsServicesOpen(false)} data-id="element-263">
              <button className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-viridia ${serviceLinks.some(link => isActive(link.path)) ? 'text-viridia' : 'text-gray-300'}`} data-id="element-264">
                Services
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} data-id="element-265" />
              </button>

              <AnimatePresence data-id="element-266">
                {isServicesOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 mt-2 w-56 bg-viridia-surface rounded-lg shadow-xl border border-gray-800 overflow-hidden" data-id="element-267">
                    <div className="py-2" data-id="element-268">
                      {serviceLinks.map(link => <Link key={link.path} to={link.path} className={`block px-4 py-2 text-sm transition-colors hover:bg-viridia-gray hover:text-viridia ${isActive(link.path) ? 'text-viridia bg-viridia-gray/50' : 'text-gray-300'}`} data-id="element-269">
                          {link.name}
                        </Link>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Case Studies Dropdown */}
            <div className="relative" onMouseEnter={() => setIsCaseStudiesOpen(true)} onMouseLeave={() => setIsCaseStudiesOpen(false)} data-id="element-270">
              <button className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-viridia ${caseStudyLinks.some(link => isActive(link.path)) ? 'text-viridia' : 'text-gray-300'}`} data-id="element-271">
                Case Studies
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isCaseStudiesOpen ? 'rotate-180' : ''}`} data-id="element-272" />
              </button>

              <AnimatePresence data-id="element-273">
                {isCaseStudiesOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 mt-2 w-56 bg-viridia-surface rounded-lg shadow-xl border border-gray-800 overflow-hidden" data-id="element-274">
                    <div className="py-2" data-id="element-275">
                      {caseStudyLinks.map(link => <Link key={link.path} to={link.path} className={`block px-4 py-2 text-sm transition-colors hover:bg-viridia-gray hover:text-viridia ${isActive(link.path) ? 'text-viridia bg-viridia-gray/50' : 'text-gray-300'}`} data-id="element-276">
                          {link.name}
                        </Link>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <Link to="/pricing" className={`text-sm font-medium transition-colors hover:text-viridia ${isActive('/pricing') ? 'text-viridia' : 'text-gray-300'}`} data-id="element-277">
              Pricing
            </Link>

            {/* Free Tools Dropdown */}
            <div className="relative" onMouseEnter={() => setIsToolsOpen(true)} onMouseLeave={() => setIsToolsOpen(false)} data-id="element-278">
              <button className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-viridia ${toolLinks.some(link => isActive(link.path)) ? 'text-viridia' : 'text-gray-300'}`} data-id="element-279">
                Free Tools
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isToolsOpen ? 'rotate-180' : ''}`} data-id="element-280" />
              </button>

              <AnimatePresence data-id="element-281">
                {isToolsOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 mt-2 w-64 bg-viridia-surface rounded-lg shadow-xl border border-gray-800 overflow-hidden" data-id="element-282">
                    <div className="py-2" data-id="element-283">
                      {toolLinks.map(link => <Link key={link.path} to={link.path} className={`block px-4 py-2 text-sm transition-colors hover:bg-viridia-gray hover:text-viridia ${isActive(link.path) ? 'text-viridia bg-viridia-gray/50' : 'text-gray-300'}`} data-id="element-284">
                          {link.name}
                        </Link>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block" data-id="element-285">
            <Link to="/contact" className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-medium text-white bg-viridia rounded-full hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_15px_rgba(16,185,129,0.4)] hover:-translate-y-0.5" data-id="element-286">
              Book a Call
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-gray-300 hover:text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-287">
            {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-288" /> : <MenuIcon className="w-6 h-6" data-id="element-289" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <AnimatePresence data-id="element-290">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="md:hidden bg-viridia-black border-t border-gray-800 overflow-hidden" data-id="element-291">
            <div className="px-4 pt-2 pb-6 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto" data-id="element-292">
              <Link to="/" className={`block px-3 py-3 rounded-md text-base font-medium ${isActive('/') ? 'text-viridia bg-viridia-surface' : 'text-gray-300 hover:text-white hover:bg-viridia-surface'}`} data-id="element-293">
                Home
              </Link>
              <Link to="/about" className={`block px-3 py-3 rounded-md text-base font-medium ${isActive('/about') ? 'text-viridia bg-viridia-surface' : 'text-gray-300 hover:text-white hover:bg-viridia-surface'}`} data-id="element-294">
                About
              </Link>

              <div className="px-3 py-2" data-id="element-295">
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-296">
                  Services
                </div>
                <div className="space-y-1 pl-3 border-l border-gray-800" data-id="element-297">
                  {serviceLinks.map(link => <Link key={link.path} to={link.path} className={`block px-3 py-2 rounded-md text-base font-medium ${isActive(link.path) ? 'text-viridia' : 'text-gray-300 hover:text-white'}`} data-id="element-298">
                      {link.name}
                    </Link>)}
                </div>
              </div>

              <div className="px-3 py-2" data-id="element-299">
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-300">
                  Case Studies
                </div>
                <div className="space-y-1 pl-3 border-l border-gray-800" data-id="element-301">
                  {caseStudyLinks.map(link => <Link key={link.path} to={link.path} className={`block px-3 py-2 rounded-md text-base font-medium ${isActive(link.path) ? 'text-viridia' : 'text-gray-300 hover:text-white'}`} data-id="element-302">
                      {link.name}
                    </Link>)}
                </div>
              </div>

              <Link to="/pricing" className={`block px-3 py-3 rounded-md text-base font-medium ${isActive('/pricing') ? 'text-viridia bg-viridia-surface' : 'text-gray-300 hover:text-white hover:bg-viridia-surface'}`} data-id="element-303">
                Pricing
              </Link>

              <div className="px-3 py-2" data-id="element-304">
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-305">
                  Free Tools
                </div>
                <div className="space-y-1 pl-3 border-l border-gray-800" data-id="element-306">
                  {toolLinks.map(link => <Link key={link.path} to={link.path} className={`block px-3 py-2 rounded-md text-base font-medium ${isActive(link.path) ? 'text-viridia' : 'text-gray-300 hover:text-white'}`} data-id="element-307">
                      {link.name}
                    </Link>)}
                </div>
              </div>

              <div className="pt-4 pb-2" data-id="element-308">
                <Link to="/contact" className="block w-full text-center px-6 py-3 text-base font-medium text-white bg-viridia rounded-md hover:bg-viridia-dark transition-colors" data-id="element-309">
                  Book a Call
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}